import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Key, X, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccountSettingsModal({ open, onClose }) {
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState('name'); // 'name' or 'password'

  const [nameData, setNameData] = useState({ new_name: user?.name || '' });
  const [nameError, setNameError] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);

  const [pwdData, setPwdData] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSaved, setPwdSaved] = useState(false);

  const rules = [
    { label: '8+ characters', test: (p) => p.length >= 8 },
    { label: 'Uppercase', test: (p) => /[A-Z]/.test(p) },
    { label: 'Lowercase', test: (p) => /[a-z]/.test(p) },
    { label: 'Number', test: (p) => /[0-9]/.test(p) }
  ];

  const handleSaveName = async (e) => {
    e.preventDefault();
    setNameError(''); setNameSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/change-name`, nameData, { withCredentials: true });
      await checkAuth();
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2000);
    } catch (err) {
      const d = err.response?.data?.detail;
      setNameError(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Failed to save'));
    } finally {
      setNameSaving(false);
    }
  };

  const handleSavePwd = async (e) => {
    e.preventDefault();
    setPwdError('');
    if (pwdData.new_password !== pwdData.confirm) { setPwdError('Passwords do not match'); return; }
    if (!rules.every(r => r.test(pwdData.new_password))) { setPwdError('Password does not meet all requirements'); return; }
    setPwdSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: pwdData.current_password, new_password: pwdData.new_password },
        { withCredentials: true }
      );
      setPwdSaved(true);
      setPwdData({ current_password: '', new_password: '', confirm: '' });
      setTimeout(() => { setPwdSaved(false); onClose(); }, 1500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setPwdError(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Failed to change password'));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-6"
          onClick={onClose}
          data-testid="account-settings-modal"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1C1C1E] border border-[#333336] rounded-2xl p-8 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold">Account Settings</h2>
              <motion.button
                whileHover={{ rotate: 90 }}
                onClick={onClose}
                data-testid="account-modal-close"
                className="p-2 rounded-lg text-[#A1A1AA] hover:bg-white/10 transition-all duration-300"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </motion.button>
            </div>

            <div className="flex gap-2 mb-6 p-1 bg-[#0A0A0A] rounded-full">
              <button
                onClick={() => setTab('name')}
                data-testid="tab-name"
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                  tab === 'name' ? 'bg-[#0A84FF] text-white' : 'text-[#A1A1AA] hover:text-white'
                }`}
              >
                <User className="w-4 h-4" strokeWidth={1.5} />
                Name
              </button>
              <button
                onClick={() => setTab('password')}
                data-testid="tab-password"
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                  tab === 'password' ? 'bg-[#0A84FF] text-white' : 'text-[#A1A1AA] hover:text-white'
                }`}
              >
                <Key className="w-4 h-4" strokeWidth={1.5} />
                Password
              </button>
            </div>

            {tab === 'name' ? (
              <form onSubmit={handleSaveName} className="space-y-4" data-testid="name-form">
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#A1A1AA]">Display Name</label>
                  <input
                    type="text"
                    value={nameData.new_name}
                    onChange={(e) => setNameData({ new_name: e.target.value })}
                    data-testid="new-name-input"
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#333336] rounded-xl text-white focus:outline-none focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF] transition-all duration-300"
                    placeholder="Your name"
                    required
                  />
                </div>
                {nameError && <div className="text-sm text-[#FF453A]" data-testid="name-error">{nameError}</div>}
                <button
                  type="submit"
                  disabled={nameSaving || nameSaved}
                  data-testid="save-name-btn"
                  className="w-full px-6 py-3 rounded-full text-sm font-medium bg-[#0A84FF] text-white hover:bg-[#0071E3] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {nameSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                    : nameSaved ? <><Check className="w-4 h-4" strokeWidth={2} /> Saved!</>
                    : 'Update Name'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSavePwd} className="space-y-4" data-testid="password-form-account">
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#A1A1AA]">Current Password</label>
                  <input
                    type="password"
                    value={pwdData.current_password}
                    onChange={(e) => setPwdData({ ...pwdData, current_password: e.target.value })}
                    data-testid="account-current-password"
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#333336] rounded-xl text-white focus:outline-none focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF] transition-all duration-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#A1A1AA]">New Password</label>
                  <input
                    type="password"
                    value={pwdData.new_password}
                    onChange={(e) => setPwdData({ ...pwdData, new_password: e.target.value })}
                    data-testid="account-new-password"
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#333336] rounded-xl text-white focus:outline-none focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF] transition-all duration-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#A1A1AA]">Confirm New Password</label>
                  <input
                    type="password"
                    value={pwdData.confirm}
                    onChange={(e) => setPwdData({ ...pwdData, confirm: e.target.value })}
                    data-testid="account-confirm-password"
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#333336] rounded-xl text-white focus:outline-none focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF] transition-all duration-300"
                    required
                  />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 p-3 bg-[#0A0A0A] rounded-lg border border-[#333336]">
                  {rules.map((r, i) => {
                    const passed = pwdData.new_password && r.test(pwdData.new_password);
                    return (
                      <span key={i} className={`text-xs ${passed ? 'text-[#32D74B]' : 'text-[#A1A1AA]'}`}>
                        {passed ? '✓' : '○'} {r.label}
                      </span>
                    );
                  })}
                </div>
                {pwdError && <div className="text-sm text-[#FF453A]" data-testid="account-pwd-error">{pwdError}</div>}
                <button
                  type="submit"
                  disabled={pwdSaving || pwdSaved}
                  data-testid="save-password-btn"
                  className="w-full px-6 py-3 rounded-full text-sm font-medium bg-[#0A84FF] text-white hover:bg-[#0071E3] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {pwdSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Changing...</>
                    : pwdSaved ? <><Check className="w-4 h-4" strokeWidth={2} /> Changed!</>
                    : 'Change Password'}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
